import { ERROR__with_assert_data } from '@shared/asserts/ERROR__with_assert_data';
import { CONSTANTS__shared } from '@shared/constants/CONSTANTS__shared';
import { ENUM__string_id_empty } from '@shared/constants/ENUM__string_id_empty';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { loge } from '@shared/utils/utils_logs';
import { ENUM__error_type } from './ENUM__error_type';
import { IAssertPayload } from './i_assert_payload';

export function try_get_stack_from_error(error: any) {
  let out = '';

  if (error instanceof Error) {
    out = error.stack || '';
  } else {
    out = new Error().stack || '';
  }

  return out;
}

/*
 * TYPE
 */

//+
export function assert_type_function(value: any, payload: IAssertPayload = {}): asserts value {
  payload = get_payload_with_type(payload, ENUM__error_type.VALIDATION_INVALID_TYPE);

  assert_condition(typeof value == 'function', payload);
}

export function assert_type_set<T>(value: any, payload: IAssertPayload = {}): asserts value is Set<T> {
  assert_true(value instanceof Set, payload);
}

export function assert_type_array<T>(value: any, payload: IAssertPayload = {}): asserts value is T[] {
  payload = get_payload_with_type(payload, ENUM__error_type.VALIDATION_INVALID_TYPE);

  assert_condition(Array.isArray(value), payload);
}

export function assert_type_object(value: any, payload: IAssertPayload = {}): asserts value {
  //because typeof null == 'object'
  assert_not_null(value, payload);

  payload = get_payload_with_type(payload, ENUM__error_type.VALIDATION_INVALID_TYPE);

  assert_condition(typeof value == 'object', payload);
}

export function assert_type_string(value: any, payload: IAssertPayload = {}): asserts value is string {
  payload = get_payload_with_type(payload, ENUM__error_type.VALIDATION_INVALID_TYPE);

  assert_condition(typeof value == 'string', payload);
}

export function assert_type_boolean(value: any, payload: IAssertPayload = {}): asserts value is boolean {
  payload = get_payload_with_type(payload, ENUM__error_type.VALIDATION_INVALID_TYPE);

  assert_condition(typeof value == 'boolean', payload);
}

export function assert_type_number(value: any, payload: IAssertPayload = {}): asserts value is number {
  payload = get_payload_with_type(payload, ENUM__error_type.VALIDATION_INVALID_TYPE);

  assert_condition(typeof value == 'number', payload);
}

export function assert_instanceof<T>(value: any, type_constructor: abstract new (...args: any[]) => T, payload: IAssertPayload = {}): asserts value is T {
  payload = get_payload_with_type(payload, ENUM__error_type.VALIDATION_INVALID_TYPE);

  assert_type_object(value, payload);

  assert_true(value instanceof type_constructor, { ...payload, type: type_constructor.name });
  //todo: remove next line if previous works fine
  // assert_equal(value.constructor.name, type_constructor.name, { ...payload, type: type_constructor.name });
}

export function assert_array_typeof_string(value_array: unknown, payload: IAssertPayload = {}): asserts value_array is string[] {
  return assert_array_typeof<string>(value_array, 'string', payload);
}

export function assert_array_typeof_number(value_array: unknown, payload: IAssertPayload = {}): asserts value_array is number[] {
  return assert_array_typeof<string>(value_array, 'number', payload);
}

export function assert_array_typeof_boolean(value_array: unknown, payload: IAssertPayload = {}): asserts value_array is boolean[] {
  return assert_array_typeof<boolean>(value_array, 'boolean', payload);
}

export function assert_array_of_object_id(value_array: unknown, payload: IAssertPayload = {}): asserts value_array is string[] {
  assert_type_array(value_array, payload);

  for (let value of value_array) {
    assert_string_object_id(value, payload);
  }
}

function assert_array_typeof<T>(value_array: unknown, type_string: string, payload: IAssertPayload = {}): asserts value_array is T[] {
  assert_type_array(value_array, payload);

  for (let value of value_array) {
    assert_equal(typeof value, type_string, payload);
  }
}

export function assert_array_instanceof<T>(
  value_array: unknown,
  type_constructor: abstract new (...args: any[]) => T,
  payload: IAssertPayload = {},
): asserts value_array is T[] {
  assert_type_array(value_array, payload);

  for (let value of value_array) {
    assert_instanceof(value, type_constructor, payload);
  }
}

/*
 * TYPE ENUM
 */

export function assert_type_enum<T extends Record<string, string | number>, V = T[keyof T]>(
  value: unknown,
  enum_object: T,
  payload: IAssertPayload = {},
): asserts value is V {
  assert_array_includes(Object.values(enum_object), value, payload);
}

export function assert_type_enum_return<T extends Record<string, string | number>, V = T[keyof T]>(
  value: unknown,
  enum_object: T,
  payload: IAssertPayload = {},
): V {
  assert_type_enum(value, enum_object, payload);

  return value as V;
}

export function assert_type_array_enum<T extends Record<string, string | number>, V = T[keyof T]>(
  value_array: unknown,
  enum_object: T,
  payload: IAssertPayload = {},
): asserts value_array is V[] {
  assert_type_array(value_array, payload);

  for (const value of value_array) {
    assert_type_enum(value, enum_object, payload);
  }
}

/*
 * EQUAL
 */

export function assert_equal<T>(value_a: T, value_b: T, payload: IAssertPayload = {}) {
  payload = get_payload_with_type(payload, ENUM__error_type.VALIDATION_INVALID_VALUE);

  assert_condition(value_a === value_b, { ...payload });
}

export function assert_not_equal<T>(value_a: T, value_b: T, payload: IAssertPayload = {}) {
  payload = get_payload_with_type(payload, ENUM__error_type.VALIDATION_INVALID_VALUE);

  assert_condition(value_a !== value_b, { ...payload, value_a, value_b });
}

/*
 * RECORD
 */

export function assert_record_value<V, K extends string | number | symbol>(data: Record<K, V>, key: K): V {
  const out = data[key];

  assert_not_null(out != null, { key });

  return out;
}

export function assert_key_in_record<K extends string | number | symbol, V>(data: Record<K, V> | object | any, key: K, payload: IAssertPayload = {}): V {
  payload = get_payload_with_type(payload, ENUM__error_type.VALIDATION_ITEM_NOT_FOUND);

  assert_condition(key in data, { ...payload, key });

  return (data as any)[key] as V;
}

/*
 * BOOLEAN
 */
export function assert_false(value: boolean, payload: IAssertPayload = {}) {
  assert_type_boolean(value, payload);

  payload = get_payload_with_type(payload, ENUM__error_type.VALIDATION_INVALID_VALUE);

  assert_condition(!value, payload);
}

export function assert_true(value: boolean, payload: IAssertPayload = {}) {
  assert_type_boolean(value, payload);

  payload = get_payload_with_type(payload, ENUM__error_type.VALIDATION_INVALID_VALUE);

  assert_condition(value, payload);
}

/*
 * NUMBER
 */

export function assert_is_safe_number(value: number | undefined | null | unknown, payload: IAssertPayload = {}): number {
  assert_type_number(value, payload);

  payload = get_payload_with_type(payload, ENUM__error_type.VALIDATION_INVALID_VALUE);

  assert_condition(Number.isFinite(value), payload);

  return value as number;
}

export function assert_number_positive(value: number, payload: IAssertPayload = {}) {
  payload = get_payload_with_type(payload, ENUM__error_type.VALIDATION_INVALID_NUMBER);

  assert_condition(value > 0, payload);
}

export function assert_number_gte(max: number, min: number, payload: IAssertPayload = {}) {
  payload = get_payload_with_type(payload, ENUM__error_type.VALIDATION_INVALID_NUMBER);

  assert_number_lte(min, max, payload);
}

export function assert_number_gt(max: number, min: number, payload: IAssertPayload = {}) {
  payload = get_payload_with_type(payload, ENUM__error_type.VALIDATION_INVALID_NUMBER);

  assert_number_lt(min, max, payload);
}

export function assert_number_lte(min: number, max: number, payload: IAssertPayload = {}) {
  payload = get_payload_with_type(payload, ENUM__error_type.VALIDATION_INVALID_NUMBER);

  assert_is_safe_number(min, payload);
  assert_is_safe_number(max, payload);

  assert_condition(min <= max, { ...payload, min, max });
}

export function assert_number_lt(min: number, max: number, payload: IAssertPayload = {}) {
  payload = get_payload_with_type(payload, ENUM__error_type.VALIDATION_INVALID_NUMBER);

  assert_is_safe_number(min, payload);
  assert_is_safe_number(max, payload);

  assert_condition(min < max, { ...payload, min, max });
}

export function assert_number_gt_0(value: number, payload: IAssertPayload = {}) {
  assert_number_gt(value, 0, payload);

  return value;
}

export function assert_number_gte_0(value: number, payload: IAssertPayload = {}) {
  assert_number_gte(value, 0, payload);

  return value;
}

export function assert_number_gte_1(value: number, payload: IAssertPayload = {}) {
  assert_number_gte(value, 1, payload);

  return value;
}

/*
 * STRING
 */

export function assert_string_is_empty(value: string | undefined | null, payload: IAssertPayload = {}): asserts value {
  assert_not_null(value, payload);

  assert_type_string(value, payload);

  payload = get_payload_with_type(payload, ENUM__error_type.STRING_SHOULD_BE_EMPTY);

  assert_condition(value.is_blank(), payload);
}

export function assert_string_not_blank(value: string | undefined | null | unknown, payload: IAssertPayload = {}): asserts value {
  assert_not_null(value, payload);

  assert_type_string(value, payload);

  payload = get_payload_with_type(payload, ENUM__error_type.STRING_CANT_BE_BLANK);

  assert_condition(!value.is_blank(), payload);
}

export function assert_string_not_empty(value: string | undefined | null | unknown, payload: IAssertPayload = {}): string {
  assert_not_null(value, payload);

  assert_type_string(value, payload);

  payload = get_payload_with_type(payload, ENUM__error_type.STRING_CANT_BE_EMPTY);

  assert_condition(value.length > 0, payload);

  return value;
}

export function assert_string_object_id(value: string | unknown, payload: IAssertPayload = {}): asserts value is string {
  let id_string = assert_string_not_empty(value);

  assert_true(id_string.is_object_id(), payload);
}

export function assert_string_object_id_not_empty(value: string | unknown, payload: IAssertPayload = {}) {
  assert_string_object_id(value, payload);

  let value_no_protocol = value.remove_string_protocol();

  assert_not_equal(value_no_protocol, ENUM__string_id_empty.OBJECT_ID, payload);

  return value;
}

export function assert_string_hash_sha256(value: string, payload: IAssertPayload = {}) {
  value = assert_string_not_empty(value, payload);

  assert_true(value.is_hash_sha256(), payload);
}

export function assert_hash_sha256_not_empty(value: string, payload: IAssertPayload = {}) {
  assert_string_hash_sha256(value, payload);

  let value_no_protocol = value.remove_string_protocol();
  let value_empty_no_protocol = ENUM__string_id_empty.HASH_SHA256_WITHOUT_PROTOCOL;

  assert_not_equal(value_no_protocol, value_empty_no_protocol, payload);

  return value;
}

export function assert_string_hash_md5(value: string, payload: IAssertPayload = {}) {
  value = assert_string_not_empty(value, payload);

  const regex_md5 = /^[a-fA-F0-9]{32}$/;

  assert_true(regex_md5.test(value.trim()), payload);
}

/*
 * NULL
 */

export function assert_is_null(value: unknown, payload: IAssertPayload = {}): asserts value {
  payload = get_payload_with_type(payload, ENUM__error_type.VALUE_SHOULD_BE_NULL);

  assert_condition(value == null, payload);
}

export function assert_not_null<T>(value: T | null | undefined, payload: IAssertPayload = {}): asserts value {
  payload = get_payload_with_type(payload, ENUM__error_type.VALUE_CANT_BE_NULL);

  assert_condition(value != null, payload);
}

export function assert_not_null_return<T>(value: T | null | undefined, payload: IAssertPayload = {}): T {
  payload = get_payload_with_type(payload, ENUM__error_type.VALUE_CANT_BE_NULL);

  assert_not_null(value, payload);

  return value as T;
}

/*
 * ARRAY
 */

export function assert_array_includes<T>(array: Array<T>, item: T, payload: IAssertPayload = {}) {
  payload = get_payload_with_type(payload, ENUM__error_type.VALIDATION_ITEM_NOT_FOUND);

  assert_condition(array.includes(item), { ...payload, item });
}

export function assert_array_not_includes<T>(array: Array<T>, item: T, payload: IAssertPayload = {}) {
  payload = get_payload_with_type(payload, ENUM__error_type.VALIDATION_ITEM_ALREADY_EXISTS);

  assert_condition(!array.includes(item), payload);
}

export function assert_array_has_1_item<T>(array: Array<T>, payload: IAssertPayload = {}) {
  payload = get_payload_with_type(payload, ENUM__error_type.VALIDATION_INVALID_VALUE);

  assert_type_array(array, payload);
  assert_condition(array.length == 1, payload);

  return array[0];
}

export function assert_array_is_empty<T>(value: Array<T>, payload: IAssertPayload = {}) {
  payload = get_payload_with_type(payload, ENUM__error_type.STRING_SHOULD_BE_EMPTY);

  assert_condition(value.is_empty(), payload);
}

export function assert_array_object_id(value: unknown[], payload: IAssertPayload = {}): asserts value is string[] {
  for (let item of value) {
    assert_string_object_id(item, payload);
  }
}

export function assert_array_not_empty<T>(value: Array<T>, payload: IAssertPayload = {}) {
  payload = get_payload_with_type(payload, ENUM__error_type.ARRAY_CANT_BE_EMPTY);

  assert_type_array(value, payload);

  assert_condition(value.length > 0, payload);
}

type ValidationCondition<T> = (value: unknown) => boolean;

export function assert_array_condition<T>(array: Array<T>, condition: ValidationCondition<T>, payload: IAssertPayload = {}): asserts array is T[] {
  assert_type_array(array, payload);

  assert_type_function(condition);

  payload = get_payload_with_type(payload, ENUM__error_type.UNKNOWN);

  array.forEach(item => assert_condition(condition(item), payload));
}

export function assert_uint8_array_not_empty(value: Uint8Array, payload: IAssertPayload = {}) {
  assert_instanceof(value, Uint8Array, payload);

  assert_condition(value.length > 0, payload);
}
/*
 * BASE
 */

function assert_condition(condition: boolean, assert_payload: IAssertPayload): asserts condition {
  if (!assert_payload.assert_type) debugger;
  if (typeof assert_payload != 'object') debugger;

  assert_payload[CONSTANTS__shared.SERIALIZATION_CLASS_FIELD_INSTANCE] = ENUM__dto_class_name.OBJECT;

  if (typeof condition == 'boolean' && condition) return;

  let omit_stack_trace = assert_payload.omit_stack_trace || false;

  loge(JSON.stringify(assert_payload), { omit_stack_trace });

  if (CONSTANTS__shared.IS_BROWSER) debugger;

  if (assert_payload.disable_debugger_breakpoint == true) {
    //do nothing
  } else {
    debugger;
  }

  throw new ERROR__with_assert_data(assert_payload.assert_type, assert_payload);
}

/*
 * METHODS
 */
export function wrong_case(): never {
  let payload = get_payload_with_type({}, ENUM__error_type.WRONG_CASE);

  assert_condition(false, payload);
}

export function workspace_access_denied(): never {
  let payload = get_payload_with_type({}, ENUM__error_type.WORKSPACE_ACCESS_DENIED);

  assert_condition(false, payload);
}

export function not_implemented(): never {
  let payload = get_payload_with_type({}, ENUM__error_type.NOT_IMPLEMENTED);

  assert_condition(false, payload);
}

export function invalid_type(type: string): never {
  let payload = get_payload_with_type({ type }, ENUM__error_type.FILE_INVALID_TYPE);

  assert_condition(false, payload);
}

export function not_implemented_type(type: string | number): never {
  let payload = get_payload_with_type({ type }, ENUM__error_type.NOT_IMPLEMENTED);

  assert_condition(false, payload);
}

function get_payload_with_type(payload: IAssertPayload, type: string) {
  let out = { ...payload };

  out.assert_type ||= type;

  return out;
}
